import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag, Sparkles, Heart, Flower2, PenTool } from 'lucide-react';
import { useTranslation } from '../i18n/LanguageContext';
const HERO_IMAGES = [{
  src: 'https://cdn.magicpatterns.com/uploads/v4nWU5iigTRC7F7xQdby14/Edit44.png',
  alt: 'FerbyStore signature pink gift box being opened, revealing a handcrafted decorated mirror'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/nQEYMtXtKVsktMC2AFtvgD/Edit45.png',
  alt: 'FerbyStore signature pink packaging boxes with a handcrafted purse'
}];
export function Hero() {
  const {
    t
  } = useTranslation();
  const [index, setIndex] = useState(0);
  // Auto-rotate the main hero image every 4.5 seconds.
  useEffect(() => {
    const id = setInterval(() => {
      setIndex(i => (i + 1) % HERO_IMAGES.length);
    }, 4500);
    return () => clearInterval(id);
  }, []);
  // Overlay always shows the OTHER image so they trade places.
  const mainImage = HERO_IMAGES[index];
  const overlayImage = HERO_IMAGES[(index + 1) % HERO_IMAGES.length];
  return <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden bg-cream-50" data-id="element-297">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-298">
        <div className="absolute top-[-10%] right-[-5%] w-[40%] h-[40%] rounded-full bg-lavender-100/40 blur-3xl" data-id="element-299" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[40%] h-[40%] rounded-full bg-blush-100/40 blur-3xl" data-id="element-300" />
      </div>

      <motion.div animate={{
      y: [0, -15, 0],
      rotate: [0, 5, 0]
    }} transition={{
      duration: 4,
      repeat: Infinity,
      ease: 'easeInOut'
    }} className="absolute top-32 left-[10%] hidden lg:block text-lavender-300" data-id="element-301">
        <Sparkles size={32} data-id="element-302" />
      </motion.div>
      <motion.div animate={{
      y: [0, 20, 0],
      rotate: [0, -10, 0]
    }} transition={{
      duration: 5,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: 1
    }} className="absolute bottom-40 right-[15%] hidden lg:block text-blush-300" data-id="element-303">
        <Flower2 size={40} data-id="element-304" />
      </motion.div>
      <motion.div animate={{
      scale: [1, 1.1, 1]
    }} transition={{
      duration: 3,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: 0.5
    }} className="absolute top-1/2 left-[5%] hidden lg:block text-gold-500/40" data-id="element-305">
        <Heart size={24} data-id="element-306" />
      </motion.div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-307">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-308">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} className="text-center lg:text-left" data-id="element-309">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-lavender-100 shadow-sm mb-8" data-id="element-310">
              <Sparkles className="w-4 h-4 text-lavender-500" data-id="element-311" />
              <span className="text-sm font-medium text-gray-600 uppercase tracking-wider" data-id="element-312">
                {t.hero.badge}
              </span>
            </div>

            <h1 className="font-serif text-5xl lg:text-6xl xl:text-7xl text-gray-900 leading-tight mb-6" data-id="element-313">
              {t.hero.titleLine1}{' '}
              <span className="text-lavender-600" data-id="element-314">{t.hero.titleLine2}</span>
            </h1>

            <p className="font-serif italic text-2xl lg:text-3xl text-gray-600 mb-8 leading-relaxed" data-id="element-315">
              {t.hero.subtitle}
            </p>

            <p className="text-lg text-gray-600 mb-10 max-w-xl mx-auto lg:mx-0" data-id="element-316">
              {t.hero.intro}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start" data-id="element-317">
              <a href="#collection" className="inline-flex items-center justify-center px-8 py-4 text-base font-medium rounded-full text-white bg-lavender-500 hover:bg-lavender-600 shadow-md hover:shadow-lg transition-all hover:-translate-y-1" data-id="element-318">
                <ShoppingBag className="w-5 h-5 mr-2" data-id="element-319" />
                {t.hero.shopCta}
              </a>
              <a href="#contact" className="inline-flex items-center justify-center px-8 py-4 text-base font-medium rounded-full text-lavender-600 bg-white border-2 border-lavender-200 hover:border-lavender-500 hover:bg-lavender-50 transition-all" data-id="element-320">
                <PenTool className="w-5 h-5 mr-2" data-id="element-321" />
                {t.hero.customCta}
              </a>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="relative" data-id="element-322">
            {/* Main image — auto-swaps between the two hero photos */}
            <motion.div animate={{
            y: [0, -8, 0]
          }} transition={{
            duration: 6,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="relative rounded-3xl overflow-hidden shadow-2xl border-8 border-white aspect-square bg-cream-100" data-id="element-323">
              <div className="absolute inset-0 bg-gradient-to-tr from-lavender-500/15 to-transparent mix-blend-overlay z-20 pointer-events-none" data-id="element-324"></div>

              <AnimatePresence mode="sync" data-id="element-325">
                <motion.img key={mainImage.src} src={mainImage.src} alt={mainImage.alt} initial={{
                opacity: 0,
                scale: 1.06
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0,
                scale: 1.02
              }} transition={{
                opacity: {
                  duration: 1.1,
                  ease: 'easeInOut'
                },
                scale: {
                  duration: 6,
                  ease: 'easeOut'
                }
              }} className="absolute inset-0 w-full h-full object-cover" data-id="element-326" />
              </AnimatePresence>

              {/* Sparkle accent */}
              <motion.div animate={{
              y: [0, -10, 0],
              rotate: [0, 12, 0]
            }} transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="absolute top-5 left-5 z-30" data-id="element-327">
                <div className="w-11 h-11 rounded-full bg-white/95 backdrop-blur-sm flex items-center justify-center shadow-md" data-id="element-328">
                  <Sparkles className="w-5 h-5 text-gold-500" data-id="element-329" />
                </div>
              </motion.div>

              {/* Slide dots */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2" data-id="element-330">
                {HERO_IMAGES.map((_, i) => <button key={i} type="button" onClick={() => setIndex(i)} aria-label={`Show image ${i + 1}`} className={`h-2 rounded-full transition-all duration-300 ${i === index ? 'w-7 bg-white shadow' : 'w-2 bg-white/60 hover:bg-white/90'}`} data-id="element-331" />)}
              </div>
            </motion.div>

            {/* Overlay card — always shows the OTHER image, so the pair trade places */}
            <motion.div initial={{
            opacity: 0,
            y: 30,
            rotate: -6
          }} animate={{
            opacity: 1,
            y: 0,
            rotate: -6
          }} transition={{
            duration: 0.9,
            delay: 0.6,
            ease: [0.22, 1, 0.36, 1]
          }} whileHover={{
            rotate: -2,
            y: -6,
            scale: 1.03
          }} className="absolute -bottom-10 -left-6 sm:-left-10 w-36 sm:w-44 lg:w-48 z-20" data-id="element-332">
              <motion.div animate={{
              y: [0, 6, 0]
            }} transition={{
              duration: 5,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 1
            }} className="rounded-2xl overflow-hidden shadow-xl border-4 border-white bg-white aspect-square relative" data-id="element-333">
                <AnimatePresence mode="sync" data-id="element-334">
                  <motion.img key={overlayImage.src} src={overlayImage.src} alt={overlayImage.alt} initial={{
                  opacity: 0,
                  scale: 1.08
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  scale: 1.02
                }} transition={{
                  opacity: {
                    duration: 1.1,
                    ease: 'easeInOut'
                  },
                  scale: {
                    duration: 6,
                    ease: 'easeOut'
                  }
                }} className="absolute inset-0 w-full h-full object-cover" data-id="element-335" />
                </AnimatePresence>
              </motion.div>

              {/* Floating heart badge on the corner */}
              <motion.div animate={{
              scale: [1, 1.1, 1]
            }} transition={{
              duration: 2.5,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="absolute -top-3 -right-3 w-9 h-9 rounded-full bg-blush-400 flex items-center justify-center shadow-md ring-4 ring-white" data-id="element-336">
                <Heart className="w-4 h-4 text-white fill-white" data-id="element-337" />
              </motion.div>
            </motion.div>

            {/* Decorative blobs */}
            <div className="absolute -top-6 -right-6 w-28 h-28 bg-gold-100 rounded-full -z-10 blur-xl" data-id="element-338"></div>
            <div className="absolute -bottom-12 left-10 w-36 h-36 bg-blush-100 rounded-full -z-10 blur-xl" data-id="element-339"></div>
            <div className="absolute top-1/3 -right-10 w-32 h-32 bg-lavender-100 rounded-full -z-10 blur-2xl" data-id="element-340"></div>
          </motion.div>
        </div>
      </div>
    </section>;
}